import { toJS } from 'mobx'
import { isEqual } from 'lodash'
import { useQueryClient } from '@tanstack/react-query'
import useSearchQuery from '@Components/Search/SearchPanel/useSearchQuery'
import { PaginationBody } from '@Entities/pagination'
import { isEmptyObject } from '@App/helpers/check-is-empty-object'
import { PartyPage, QueryPartyStore } from '@App/stores/search/party/query-party-store'

export const PARTY_SEARCH_QUERY_KEY = 'party-search'

/**
 * Runs a party store's primary search through TanStack Query. The store owns the query, page and page size (the
 * key is built from them), so a submit, a page change and a limit change are all just store writes. Pass
 * `undefined` for a store that still searches imperatively; nothing is fetched then.
 *
 * The results are copied into the store from the query's data, not from the fetch, so a cache hit (which never
 * runs the fetch) reaches the store too.
 */
export const usePartySearchQuery = <resultType, query extends object>(
    store: QueryPartyStore<resultType, query> | undefined
) => {
    const queryClient = useQueryClient()
    const searchQuery = store ? toJS(store.partyQuery) : undefined
    const pagination: PaginationBody = { page: store?.currentPage ?? 1, limit: store?.resultsPerPageLimit ?? 10 }
    const scope = store?.partySearchScope

    const partyQuery = useSearchQuery<PartyPage<resultType>>({
        queryKey: [PARTY_SEARCH_QUERY_KEY, scope, searchQuery, pagination],
        queryFn: async () => {
            if (!store || !searchQuery) {
                return { parties: [], count: 0 }
            }
            return store.fetchParties(searchQuery, pagination)
        },
        initialData: store?.restoredPartyPage,
        applyData: (page) => store?.applyPartyResults(page, pagination),
        // Reset (and a fresh visit) leaves the query empty; a blank search should not hit the API on its own.
        enabled: !!searchQuery && !isEmptyObject(searchQuery),
        onError: (error) => store?.reportSearchError(error),
    })

    const search = (submittedQuery: query) => {
        if (!store) {
            return
        }
        // Resubmitting identical criteria on page 1 leaves the query key unchanged, so a key-driven refetch alone
        // wouldn't fire — e.g. retrying after a failed search with the same criteria.
        const keyWillBeUnchanged = isEqual(searchQuery, submittedQuery) && pagination.page === 1
        store.startPartySearch(submittedQuery)
        if (keyWillBeUnchanged) {
            queryClient.invalidateQueries({ queryKey: [PARTY_SEARCH_QUERY_KEY, scope] })
        }
    }

    return { isFetching: partyQuery.isFetching, search }
}

export default usePartySearchQuery
